import { useEffect, useState } from "react";
import { useLocale } from "./i18n/locale-context";
import type { Lang } from "./i18n/config";

/**
 * Var användaren befinner sig: i den installerade appen (hemskärmen) eller på
 * sajten i en webbläsare. Vi länkar aldrig ut från appen – texterna byter bara ord.
 */
export type Surface = "app" | "web";

/** Läser av display-mode. SSR-säker: returnerar alltid "web" på servern. */
export function detectSurface(): Surface {
  if (typeof window === "undefined") return "web";
  const nav = window.navigator as Navigator & { standalone?: boolean };
  const standalone =
    ["standalone", "fullscreen", "minimal-ui"].some(
      (mode) => window.matchMedia?.(`(display-mode: ${mode})`).matches,
    ) || nav.standalone === true;
  return standalone ? "app" : "web";
}

/** True när Sellavo körs som installerad app. Uppdateras efter hydrering. */
export function useIsAppSurface(): boolean {
  const [isApp, setIsApp] = useState(false);
  useEffect(() => {
    setIsApp(detectSurface() === "app");
  }, []);
  return isApp;
}

export type SurfaceWords = {
  /** "appen" eller "sajten" */
  noun: string;
  /** "i appen" eller "på sajten" */
  inPlace: string;
  /** "här i appen" eller "här på sajten" */
  here: string;
};

export function surfaceWords(isApp: boolean, lang: Lang = "sv"): SurfaceWords {
  if (lang === "en") {
    return isApp
      ? { noun: "app", inPlace: "in the app", here: "here in the app" }
      : { noun: "website", inPlace: "on the website", here: "here on the website" };
  }
  if (lang === "es") {
    return isApp
      ? { noun: "aplicación", inPlace: "en la aplicación", here: "aquí en la aplicación" }
      : { noun: "sitio web", inPlace: "en el sitio web", here: "aquí en el sitio web" };
  }
  return isApp
    ? { noun: "appen", inPlace: "i appen", here: "här i appen" }
    : { noun: "sajten", inPlace: "på sajten", here: "här på sajten" };
}

/** Hook-varianten – används i komponenter. */
export function useSurfaceWords(): SurfaceWords {
  const { lang } = useLocale();
  return surfaceWords(useIsAppSurface(), lang);
}

/**
 * Ersätter platshållare i textkonstanter:
 * {plats} → "appen"/"sajten", {här} → "här i appen"/"här på sajten",
 * {på} → "i appen"/"på sajten".
 */
export function fillSurface(text: string, words: SurfaceWords): string {
  return text
    .replaceAll("{plats}", words.noun)
    .replaceAll("{här}", words.here)
    .replaceAll("{på}", words.inPlace);
}
